import { Variants } from "framer-motion";
import { ANIMATION_CONFIG, ANIMATION_STATE, LOOP_CONFIG } from "./constants";
import { calculatePulseLoopTimes, getExitTransition } from "./helpers";

const { PULSE_CYCLE_DURATION, BONUS_TEXT_Y_SHIFT_OUT } = ANIMATION_CONFIG;

const TEXT_Y_BASE = -8;
const TEXT_Y_UP_SMALL = TEXT_Y_BASE;
const TEXT_Y_UP_BIG = TEXT_Y_BASE - BONUS_TEXT_Y_SHIFT_OUT;

const yKeyframesGiftText = [
  -20,
  TEXT_Y_BASE,
  TEXT_Y_BASE,
  TEXT_Y_BASE,
  TEXT_Y_BASE,
  TEXT_Y_UP_BIG,
  TEXT_Y_UP_BIG,
  TEXT_Y_UP_SMALL,
  TEXT_Y_UP_SMALL,
  TEXT_Y_UP_BIG,
  TEXT_Y_UP_BIG,
  TEXT_Y_UP_SMALL,
  TEXT_Y_UP_SMALL,
  TEXT_Y_UP_BIG,
  TEXT_Y_UP_BIG,
  TEXT_Y_UP_SMALL,
  TEXT_Y_UP_SMALL,
  TEXT_Y_BASE,
  TEXT_Y_BASE,
];

const opacityKeyframesGiftText = [
  0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
];

const opacityKeyframesFinalGlow = [
  0,
  0,
  0,
  0,
  0,
  1,
  1,
  0,
  0,
  1,
  1,
  0,
  0,
  1,
  1,
  0,
  0,
  0,
  0,
];

const opacityKeyframesGiftGlow = opacityKeyframesFinalGlow;

const yKeyframesGiftText_PulseLoop = [
  yKeyframesGiftText[3],
  yKeyframesGiftText[4],
  yKeyframesGiftText[5],
  yKeyframesGiftText[6],
  yKeyframesGiftText[3],
];
const opacityKeyframesGiftText_PulseLoop = [
  opacityKeyframesGiftText[3],
  opacityKeyframesGiftText[4],
  opacityKeyframesGiftText[5],
  opacityKeyframesGiftText[6],
  opacityKeyframesGiftText[3],
];
const opacityKeyframesFinalGlow_PulseLoop = [
  opacityKeyframesFinalGlow[3],
  opacityKeyframesFinalGlow[4],
  opacityKeyframesFinalGlow[5],
  opacityKeyframesFinalGlow[6],
  opacityKeyframesFinalGlow[3],
];
const opacityKeyframesGiftGlow_PulseLoop = [
  opacityKeyframesGiftGlow[3],
  opacityKeyframesGiftGlow[4],
  opacityKeyframesGiftGlow[5],
  opacityKeyframesGiftGlow[6],
  opacityKeyframesGiftGlow[3],
];
const pulseLoopTimes = calculatePulseLoopTimes();

export const initialGlowVariant: Variants = {
  [ANIMATION_STATE.IDLE]: {
    opacity: 0,
    x: "-50%",
    y: 100,
    transition: { duration: 0 },
  },

  [ANIMATION_STATE.ENTERING]: {
    opacity: [0, 1],
    x: "-50%",
    y: [100, 0],
    transition: {
      opacity: { ease: "easeOut", duration: 0.2 },
      y: { ease: "easeOut", duration: 0.4 },
      x: { duration: 0 },
    },
  },

  [ANIMATION_STATE.PULSING_1]: {
    opacity: 1,
    x: "-50%",
    y: 0,
    transition: { duration: 0 },
  },

  [ANIMATION_STATE.PULSING_2]: {
    opacity: 1,
    x: "-50%",
    y: 0,
    transition: { duration: 0 },
  },

  [ANIMATION_STATE.EXIT]: {
    opacity: 0,
    x: "-50%",
    y: 100,
    transition: { delay: 2.5, duration: 0.6, ease: "linear" },
  },
};

export const registerGiftTextVariant: Variants = {
  [ANIMATION_STATE.IDLE]: {
    opacity: 0,
    y: 100,
    transition: { duration: 0 },
  },

  [ANIMATION_STATE.ENTERING]: {
    opacity: [0, 1],
    y: [100, TEXT_Y_BASE],
    transition: {
      opacity: { ease: "easeOut", duration: 1.2 },
      y: { ease: "easeOut", duration: 1.2 },
    },
  },

  [ANIMATION_STATE.PULSING_1]: {
    opacity: opacityKeyframesGiftText_PulseLoop,
    y: yKeyframesGiftText_PulseLoop,
    transition: {
      opacity: { ease: "linear", duration: PULSE_CYCLE_DURATION, times: pulseLoopTimes },
      y: { ease: "linear", duration: PULSE_CYCLE_DURATION, times: pulseLoopTimes },
    },
  },

  [ANIMATION_STATE.PULSING_2]: {
    opacity: opacityKeyframesGiftText_PulseLoop,
    y: yKeyframesGiftText_PulseLoop,
    transition: {
      opacity: { ease: "linear", duration: PULSE_CYCLE_DURATION, times: pulseLoopTimes },
      y: { ease: "linear", duration: PULSE_CYCLE_DURATION, times: pulseLoopTimes },
    },
  },

  [ANIMATION_STATE.EXIT]: {
    opacity: 0,
    y: 100,
    transition: { delay: 1, duration: 0.6, ease: "backIn" },
  },
};

export const finalGlowVariant: Variants = {
  [ANIMATION_STATE.IDLE]: {
    opacity: 0,
    x: "-50%",
    y: 100,
    transition: { duration: 0 },
  },

  [ANIMATION_STATE.ENTERING]: {
    opacity: 0,
    x: "-50%",
    y: [100, 0],
    transition: {
      y: { ease: "easeOut", duration: 0.8 },
      opacity: { duration: 0 },
      x: { duration: 0 },
    },
  },

  [ANIMATION_STATE.PULSING_1]: {
    opacity: opacityKeyframesFinalGlow_PulseLoop,
    x: "-50%",
    y: 0,
    transition: {
      opacity: { ease: "linear", duration: PULSE_CYCLE_DURATION, times: pulseLoopTimes },
    },
  },

  [ANIMATION_STATE.PULSING_2]: {
    opacity: opacityKeyframesFinalGlow_PulseLoop,
    x: "-50%",
    y: 0,
    transition: {
      opacity: { ease: "linear", duration: PULSE_CYCLE_DURATION, times: pulseLoopTimes },
    },
  },

  [ANIMATION_STATE.EXIT]: {
    opacity: 0,
    x: "-50%",
    y: 100,
    transition: { delay: 1, duration: 0.6, ease: "backIn" },
  },
};
